import {ProjectStrategy, AdminRequestsTableDeps, OnQueryChangeContext, OnQueryChangeCtx} from './project-strategy.interface';
import {DocumentTypeMap} from './document-type-map';
import {setDocumentTypeMap} from './document-type-helpers';
import {ConsumeMode, OperationType, Request, RequestState, RequestWithDuplicata, SearchPaymentQueryParams, TransformedRequestWithDuplicata} from './request';
import {Document, User} from './user';
import {ApiService} from '../services/api.service';
import {CartService, FormApplication} from '../services/cart.service';
import {RequestService} from '../services/request.service';
import {PlacesService} from '../services/places.service';
import {ScanDocumentService} from '../services/scan-document.service';
import {PrintService} from '../services/print.service';
import {StatusLabel} from './print';
import {TableConfig, TableDataTransformer} from 'ngx-satoris';
import {FilterWithType} from 'ngx-satoris/lib/shared/models/filters';
import {PaginationQuery, SortQuery} from 'ngx-satoris/lib/shared/models/params';
import {UiPermission} from './permission';
import {AppEnvironment} from 'src/environments/environment.model';
import {environment} from 'src/environments/environment';
import {hasValue} from '../utils/form';
import {VisaFormConfig} from './forms';
import {NOTICE_FORM_CONFIG} from './notice-form';

export abstract class BaseProjectStrategy implements ProjectStrategy {
  abstract readonly referenceSchema: string;
  abstract readonly documentTypeMap: DocumentTypeMap;
  abstract readonly tableRequestsConfig: TableConfig;

  protected initDocumentTypeMap(): void {
    setDocumentTypeMap(this.documentTypeMap);
  }

  canPrintSticker(_op: OperationType, _req: RequestWithDuplicata, _scan: ScanDocumentService): boolean {
    return false;
  }

  getStickerStatusFromRequest(_req: RequestWithDuplicata): string {
    return '';
  }

  getStatusLabelsFromRequest(_req: RequestWithDuplicata | Request, _print: PrintService): StatusLabel | null {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getStatusTheme(_request: any, _requestService: any): unknown {
    return null;
  }

  isRequirePreapproval(_req: RequestWithDuplicata, _env: AppEnvironment, _request?: Request): boolean | undefined {
    return false;
  }

  canHaveDocuments(_current: RequestWithDuplicata): boolean {
    return true;
  }

  shouldShowVisaNo(_operationId: string | undefined): boolean {
    return true;
  }

  showWarning(_req: Request, _request: RequestService, _cart: CartService): boolean {
    return false;
  }

  canTrackTimeslot(req: RequestWithDuplicata): boolean {
    return environment.formHasBookTimeSlotStep && (req.state === RequestState.PAID || req.state === RequestState.DELAY_PAY);
  }

  enrollmentNoticeKey(req: RequestWithDuplicata): string | null {
    const datesValid = hasValue(req.usageAfter, 'date') && hasValue(req.usageUntil, 'date');
    const stateOk = req.state === RequestState.PAID || req.state === RequestState.DELAY_PAY || req.state === RequestState.USED;
    if(!datesValid || !stateOk) return null;
    return (environment.formHasBookTimeSlotStep && (!req.timeslot || !req.timeslot_place_id)) ? 'timeslot.noBooking' : 'afterAndUntil';
  }

  evaluateCanBeAcceptedRefused(_current: RequestWithDuplicata): boolean {
    return false;
  }

  evaluateCanBeReviewed(_current: RequestWithDuplicata, _user: User): boolean {
    return false;
  }

  isPortOnlinePayable(_self: RequestService, _request: Request): boolean {
    return false;
  }

  isCanHaveOnArrival(_current: {countryCat?: string}): boolean {
    return true;
  }

  canAccessWebKiosk(_country: string): boolean {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  applySchemaValidations(_component: any, _documentType?: any): void { /* no-op */ }

  documentTypeIsVisaExtAB(_operationId: string): boolean {
    return false;
  }

  documentTypeIsVisaExtC(_operationId: string): boolean {
    return false;
  }

  documentTypeIsAnyExtension(_operationId: string): boolean {
    return false;
  }

  documentTypeIsVisaArrivalKaza(_operationId: string): boolean {
    return false;
  }

  getDocumentTypeIsVisaExtC(): string | undefined {
    return undefined;
  }

  preprocessFiltersForBackend(ctx: OnQueryChangeContext): FilterWithType[] {
    return ctx.filters;
  }

  updateColumnsVisibilityForFilters(_ctx: OnQueryChangeContext): void { /* no-op */ }

  getExtensionType(paymentRequests: Document[], _meta: unknown): Document | undefined {
    return paymentRequests[0];
  }

  checkVisaPreconditions(_current: RequestWithDuplicata): string | undefined {
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hasUiPerm(this: any, _label: UiPermission): boolean {
    return false;
  }

  handleAdminRequestsOnQueryChange(ctx: OnQueryChangeCtx): void {
    const {e, config, loader, smartTable, tableDataTransformer, getApplications} = ctx;
    if(!e.pagination.isPreload) {
      loader.loading(true);
    }
    this.updateColumnsVisibilityForFilters({filters: e.filters, config, isPreload: e.pagination.isPreload});
    e.filters = this.preprocessFiltersForBackend({filters: e.filters, config, isPreload: e.pagination.isPreload});

    getApplications(e.pagination, e.filters, e.sort).then((res) => {
      if(tableDataTransformer) {
        smartTable.updateTableData(res as TransformedRequestWithDuplicata[], e.pagination.isPreload, e.pagination.offset);
      }
      loader.loading(false);
    }).catch(error => {
      loader.loading(true, {type: 'error', message: 'Error fetching applications: ' + error});
    });
  }

  searchApplications(api: ApiService, pagination: PaginationQuery, filters: FilterWithType[], sort: SortQuery): Promise<unknown> {
    const built = this.buildSearchParamsForPayments({filters, sort});
    return api.searchPayment(built.query, built.sortColumn, built.sortOrder ?? undefined, pagination.offset, pagination.limit);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected getSchemaRootWithProperties(inputSchema: any): any {
    try {
      if(typeof inputSchema === 'string') {
        const parsed = JSON.parse(inputSchema);
        inputSchema = parsed;
      }
    } catch (_e) { /* ignore parse errors */ }
    if(inputSchema?.properties) return inputSchema;
    if(inputSchema?.schema?.properties) return inputSchema.schema;
    // Fallback: shallow search for first object with properties
    const queue: any[] = [inputSchema];
    while(queue.length) {
      const node = queue.shift();
      if(node && typeof node === 'object') {
        if(node.properties) return node;
        for(const key of Object.keys(node)) {
          const val = node[key];
          if(val && typeof val === 'object') queue.push(val);
        }
      }
    }
    return inputSchema;
  }

  abstract setCountries(api: ApiService): Promise<boolean>;
  abstract isCountryCat(country: string, cat: string): boolean;
  abstract setCurrentCountryCat(current: {countryCat?: string; indexed?: unknown}): string | undefined;
  abstract getCountries(previousVisaRequiredOn: boolean): {value: string; name: string}[];
  abstract loadFormConfigs(): {[id: string]: VisaFormConfig};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abstract getFormConfig(docType: any, currentRequest: Request, api?: ApiService): string | undefined;
  getNoticeFormConfig(): VisaFormConfig {
    return NOTICE_FORM_CONFIG;
  }
  abstract getVisaTypes(all: Document[], api: ApiService, cart: CartService, place: PlacesService, consumeMode: ConsumeMode): [boolean, Document[]];
  abstract getRequestData(self: RequestService, request: Request, application: FormApplication): Record<string, unknown>;
  abstract buildAdminRequestsTableTransformer(deps: AdminRequestsTableDeps): TableDataTransformer<unknown>;
  buildAdminRequestsTableConfig(deps: AdminRequestsTableDeps): TableConfig {
    const tableConfig = JSON.parse(JSON.stringify(this.tableRequestsConfig));
    tableConfig.dataTransformer = this.buildAdminRequestsTableTransformer(deps);
    tableConfig.storagePrefix = String(deps.userId);
    return tableConfig;
  }

  abstract buildSearchParamsForPayments(input: {filters: FilterWithType[]; sort: SortQuery}): {
    query: SearchPaymentQueryParams;
    sortColumn: 'id' | 'createdAt' | 'isUrgent' | 'timeslot' | undefined;
    sortOrder: 'ASC' | 'DESC' | null;
  };
}
